import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormModal } from '@/components/ui/form-modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Textarea } from '@/components/ui/textarea';
import type { OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import { NotificationType } from 'hooks/useNotification';
import {
  AUTOMATION_CHANNEL_OPTIONS,
  AUTOMATION_PRIORITY_OPTIONS,
} from './automationNotifyForm';
import {
  toDisplayTemplate,
  toStoredTemplate,
} from './eventNameTemplatePreview';
import {
  MultiIdCombobox,
  type MultiIdComboboxOption,
} from '@/components/ui/multi-id-combobox';
import { PlanActionDrawer } from '../PlanLibraryTab/PlanActionDrawer';
import {
  checklistItemToPlanTask,
  planTaskToChecklistItem,
} from './checklistOrgActionModel';

/** Which per-channel message field each channel writes to. */
const CHANNEL_MESSAGE_FIELD: Record<
  string,
  'appMessage' | 'emailMessage' | 'smsMessage' | 'teamsMessage' | 'phoneMessage'
> = {
  [NotificationType.IN_APP]: 'appMessage',
  [NotificationType.EMAIL]: 'emailMessage',
  [NotificationType.SMS]: 'smsMessage',
  [NotificationType.TEAMS]: 'teamsMessage',
  [NotificationType.TTS_CELL]: 'phoneMessage',
};

const LOCKED_CHANNELS = new Set<string>([
  NotificationType.TEAMS,
  NotificationType.TTS_CELL,
]);

function channelLabel(value: string): string {
  if (value === NotificationType.TEAMS) return 'Microsoft Teams';
  if (value === NotificationType.TTS_CELL) return 'Voice (TTS cell)';
  return (
    AUTOMATION_CHANNEL_OPTIONS.find((opt) => opt.value === value)?.label ??
    value
  );
}

function priorityLabel(value: string): string {
  return (
    AUTOMATION_PRIORITY_OPTIONS.find((opt) => opt.value === value)?.label ??
    value
  );
}

const NOTIFY_TEMPLATE_FIELDS = [
  'titleTemplate',
  'bodyTemplate',
  'emailSubject',
  'emailMessage',
  'smsMessage',
  'teamsMessage',
  'phoneMessage',
  'appMessage',
] as const;

function identityTemplate(value: string | undefined): string {
  return value ?? '';
}

/**
 * Templates are stored as `{token}` but edited as `@token`, so translate every
 * message field on the way in and back out again.
 */
function convertNotifyTemplates(
  item: OrgEventChecklistItemDef,
  convert: (value: string | undefined) => string
): OrgEventChecklistItemDef {
  if (!item.notify) return item;
  const notify = { ...item.notify };
  for (const key of NOTIFY_TEMPLATE_FIELDS) {
    const value = notify[key];
    if (typeof value === 'string') notify[key] = convert(value);
  }
  return { ...item, notify };
}

type ChecklistItemEditModalProps = {
  item: OrgEventChecklistItemDef | null;
  /** A blank draft that is only appended to the rule once saved. */
  isNew?: boolean;
  /** Roster options for the org action POC and notification recipients. */
  userOptions: MultiIdComboboxOption[];
  teamOptions: MultiIdComboboxOption[];
  /**
   * Automation rules edit `{token}` as `@token`. Manual event create stores
   * literal text, so skip that conversion and any mention chips.
   */
  allowTemplateVariables?: boolean;
  /** Other org-action items on this checklist, for task dependencies. */
  siblingItems?: OrgEventChecklistItemDef[];
  /** Create Event tasks assign a person or a role, and a Responsible AOR list. */
  assignPersonOrRole?: boolean;
  defaultAorIds?: string[];
  onOpenChange: (open: boolean) => void;
  onSave: (next: OrgEventChecklistItemDef) => void;
};

/**
 * Edits a checklist _template_ on an automation rule. Deliberately does not
 * reuse ActionDrawer / ChecklistNotifyComposeModal, which act on real entities
 * and would create or send records while the rule is still being edited.
 */
export function ChecklistItemEditModal({
  item,
  isNew = false,
  userOptions,
  teamOptions,
  allowTemplateVariables = true,
  siblingItems = [],
  assignPersonOrRole = false,
  defaultAorIds = [],
  onOpenChange,
  onSave,
}: ChecklistItemEditModalProps) {
  const toEditor = allowTemplateVariables
    ? toDisplayTemplate
    : identityTemplate;
  const toStored = allowTemplateVariables ? toStoredTemplate : identityTemplate;

  const [draft, setDraft] = useState<OrgEventChecklistItemDef | null>(() =>
    item ? convertNotifyTemplates(item, toEditor) : null
  );
  const [activeChannel, setActiveChannel] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);

  useEffect(() => {
    const convert = allowTemplateVariables
      ? toDisplayTemplate
      : identityTemplate;
    setDraft(item ? convertNotifyTemplates(item, convert) : null);
    setActiveChannel(null);
    setBodyError(null);
  }, [item, allowTemplateVariables]);

  if (!draft) return null;

  const isNotify = draft.kind === 'send_notification';

  if (!isNotify) {
    return (
      <PlanActionDrawer
        task={isNew ? null : checklistItemToPlanTask(draft)}
        siblingTasks={siblingItems
          .filter((row) => row.kind === 'org_action' && row.id !== draft.id)
          .map(checklistItemToPlanTask)}
        workspaceLabel="Response Plan"
        showClassification={false}
        assignPersonOrRole={assignPersonOrRole}
        defaultAorIds={defaultAorIds}
        onClose={() => onOpenChange(false)}
        onSave={(task) => {
          onSave(planTaskToChecklistItem(task, draft));
          onOpenChange(false);
        }}
      />
    );
  }
  const channels = (
    draft.notify?.notificationTypes?.length
      ? draft.notify.notificationTypes
      : [NotificationType.IN_APP]
  ).filter((channel) => !LOCKED_CHANNELS.has(channel));
  const activeChannels = channels.length ? channels : [NotificationType.IN_APP];
  const missingBodies = activeChannels.filter(
    (channel) => !(draft.notify?.[CHANNEL_MESSAGE_FIELD[channel]] ?? '').trim()
  );

  const patchNotify = (
    patch: Partial<NonNullable<OrgEventChecklistItemDef['notify']>>
  ) =>
    setDraft((current) =>
      current
        ? { ...current, notify: { ...current.notify, ...patch } }
        : current
    );

  const title = `${isNew ? 'New' : 'Edit'} notification template`;

  return (
    <FormModal
      open
      onOpenChange={onOpenChange}
      title={title}
      widthClassName="tw:!max-w-[45rem] tw:sm:!max-w-[45rem]"
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              if (missingBodies.length > 0) {
                setBodyError(
                  `Add a message for ${missingBodies.map(channelLabel).join(', ')}.`
                );
                setActiveChannel(missingBodies[0]);
                return;
              }
              setBodyError(null);
              onSave(
                convertNotifyTemplates(
                  {
                    ...draft,
                    label: draft.label.trim() || 'Send Notification',
                    notify: {
                      ...draft.notify,
                      notificationTypes: activeChannels,
                    },
                  },
                  toStored
                )
              );
              onOpenChange(false);
            }}
          >
            Done
          </Button>
        </>
      }
    >
      <div className="tw:flex tw:flex-col tw:gap-3">
        <>
          <Field>
            <FieldLabel>Communication Channels</FieldLabel>
            <div className="tw:flex tw:flex-wrap tw:gap-2">
              {AUTOMATION_CHANNEL_OPTIONS.map((opt) => {
                const locked = LOCKED_CHANNELS.has(opt.value);
                const checked = activeChannels.includes(opt.value);
                const label = channelLabel(opt.value);
                if (locked) {
                  return (
                    <TooltipProvider key={opt.value}>
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <span className="tw:inline-flex tw:cursor-not-allowed tw:items-center tw:gap-1.5 tw:text-xs tw:opacity-60" />
                          }
                        >
                          <Checkbox checked={false} disabled />
                          {label}
                        </TooltipTrigger>
                        <TooltipContent>
                          {opt.value === NotificationType.TEAMS
                            ? 'Upgrade to Enable'
                            : 'Upgrade to Enable'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }
                return (
                  <label
                    key={opt.value}
                    className="tw:flex tw:items-center tw:gap-1.5 tw:text-xs"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => {
                        const next = checked
                          ? activeChannels.filter((t) => t !== opt.value)
                          : [...activeChannels, opt.value];
                        patchNotify({
                          notificationTypes: next.length
                            ? next
                            : [NotificationType.IN_APP],
                        });
                      }}
                    />
                    {label}
                  </label>
                );
              })}
            </div>
          </Field>

          <Field>
            <FieldLabel>Channel messages</FieldLabel>
            <Tabs
              value={
                activeChannel && activeChannels.includes(activeChannel)
                  ? activeChannel
                  : activeChannels[0]
              }
              onValueChange={(next) => setActiveChannel(String(next))}
            >
              <TabsList className="tw:w-full">
                {activeChannels.map((channel) => (
                  <TabsTrigger key={channel} value={channel}>
                    {channelLabel(channel)}
                  </TabsTrigger>
                ))}
              </TabsList>
              {activeChannels.map((channel) => (
                <TabsContent
                  key={channel}
                  value={channel}
                  className="tw:space-y-2 tw:pt-2"
                >
                  {channel === NotificationType.EMAIL ? (
                    <Field>
                      <FieldLabel>Email subject</FieldLabel>
                      <Input
                        value={draft.notify?.emailSubject ?? ''}
                        onChange={(e) =>
                          patchNotify({ emailSubject: e.target.value })
                        }
                      />
                    </Field>
                  ) : null}
                  <Field>
                    <FieldLabel>{channelLabel(channel)} message</FieldLabel>
                    <Textarea
                      rows={3}
                      value={
                        draft.notify?.[CHANNEL_MESSAGE_FIELD[channel]] ?? ''
                      }
                      onChange={(e) =>
                        patchNotify({
                          [CHANNEL_MESSAGE_FIELD[channel]]: e.target.value,
                        })
                      }
                    />
                  </Field>
                </TabsContent>
              ))}
            </Tabs>
            {bodyError ? (
              <p className="tw:m-0 tw:text-sm tw:text-destructive">
                {bodyError}
              </p>
            ) : null}
          </Field>

          <Field>
            <FieldLabel>Recipients</FieldLabel>
            <FieldDescription>
              Prefills the compose sheet when someone sends this from the
              Response Plan. The automation never sends it for you.
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel>Users</FieldLabel>
            <MultiIdCombobox
              options={userOptions}
              value={draft.notify?.userIds ?? []}
              onChange={(next) => patchNotify({ userIds: next })}
              placeholder="Select users…"
            />
          </Field>

          <Field>
            <FieldLabel>Roles</FieldLabel>
            <MultiIdCombobox
              options={teamOptions}
              value={draft.notify?.teamIds ?? []}
              onChange={(next) => patchNotify({ teamIds: next })}
              placeholder="Select roles…"
            />
          </Field>

          <Field>
            <FieldLabel>Severity</FieldLabel>
            <Select
              value={draft.notify?.priority ?? 'watch'}
              onValueChange={(v) => patchNotify({ priority: String(v) })}
            >
              <SelectTrigger>
                <SelectValue>
                  {priorityLabel(draft.notify?.priority ?? 'watch')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="dark">
                {AUTOMATION_PRIORITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </>
      </div>
    </FormModal>
  );
}
